import {
	forwardRef,
	useEffect,
	useId,
	type ButtonHTMLAttributes,
	type InputHTMLAttributes,
	type ReactNode,
	type SelectHTMLAttributes
} from 'react';
import './ui.css';

/**
 * Keystone UI.
 *
 * HeroUI v3's component system, rebuilt in plain CSS and React. The geometry,
 * timings and variant vocabulary are transcribed from their source (see
 * ui.css); only the palette differs, because HeroUI ships a blue accent and
 * Keystone is monochrome.
 *
 * Variant names deliberately match theirs -- primary / secondary / tertiary /
 * outline / ghost / danger / danger-soft -- so their docs and examples read
 * across to this code without translation.
 *
 * No Tailwind, no framer-motion, ~2KB gzipped rather than ~130KB, and nothing
 * to rewrite when their API next moves.
 */

type Size = 'sm' | 'md' | 'lg';

/* ----------------------------------------------------------------- button */

export type ButtonVariant =
	| 'primary'
	| 'secondary'
	| 'tertiary'
	| 'outline'
	| 'ghost'
	| 'danger'
	| 'danger-soft';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: Size;
	block?: boolean;
	iconOnly?: boolean;
	loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
	{
		variant = 'primary',
		size = 'md',
		block,
		iconOnly,
		loading,
		disabled,
		className,
		children,
		type = 'button',
		...rest
	},
	ref
) {
	return (
		<button
			ref={ref}
			type={type}
			// A loading button must not also be clickable, or a slow request gets
			// fired twice by an impatient second click.
			disabled={disabled || loading}
			className={[
				'u-btn',
				`u-btn--${variant}`,
				`u-btn--${size}`,
				block ? 'u-btn--full-width' : '',
				iconOnly ? 'u-btn--icon-only' : '',
				className ?? ''
			]
				.filter(Boolean)
				.join(' ')}
			{...rest}
		>
			{loading && <span className="u-spinner" aria-hidden="true" />}
			{children}
		</button>
	);
});

/* ------------------------------------------------------------------ input */

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
	label?: string;
	description?: string;
	errorMessage?: string;
	size?: Size;
	startContent?: ReactNode;
	endContent?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
	{
		label,
		description,
		errorMessage,
		size = 'md',
		startContent,
		endContent,
		className,
		id,
		disabled,
		...rest
	},
	ref
) {
	const generated = useId();
	const inputId = id ?? generated;
	const helpId = `${inputId}-help`;
	const invalid = Boolean(errorMessage);

	return (
		<div className="u-field">
			{label && (
				<label className={`u-label${invalid ? ' u-label--invalid' : ''}`} htmlFor={inputId}>
					{label}
				</label>
			)}

			<div
				className={[
					'u-input-wrap',
					`u-input-wrap--${size}`,
					invalid ? 'is-invalid' : '',
					disabled ? 'is-disabled' : ''
				]
					.filter(Boolean)
					.join(' ')}
			>
				{startContent && <span className="u-input-affix">{startContent}</span>}
				<input
					ref={ref}
					id={inputId}
					className={['u-input', className ?? ''].filter(Boolean).join(' ')}
					disabled={disabled}
					aria-invalid={invalid || undefined}
					aria-describedby={description || errorMessage ? helpId : undefined}
					{...rest}
				/>
				{endContent && <span className="u-input-affix">{endContent}</span>}
			</div>

			{(description || errorMessage) && (
				<span
					id={helpId}
					className={`u-description${invalid ? ' u-description--error' : ''}`}
				>
					{errorMessage || description}
				</span>
			)}
		</div>
	);
});

/* ------------------------------------------------------------------- card */

export function Card({
	children,
	variant = 'bordered',
	className
}: {
	children: ReactNode;
	variant?: 'default' | 'bordered' | 'transparent';
	className?: string;
}) {
	return (
		<div
			className={['u-card', `u-card--${variant}`, className ?? ''].filter(Boolean).join(' ')}
		>
			{children}
		</div>
	);
}

export const CardTitle = ({ children }: { children: ReactNode }) => (
	<h2 className="u-card__title">{children}</h2>
);
export const CardDescription = ({ children }: { children: ReactNode }) => (
	<p className="u-card__description">{children}</p>
);
export const CardBody = ({ children }: { children: ReactNode }) => (
	<div className="u-card__content">{children}</div>
);
export const CardFooter = ({ children }: { children: ReactNode }) => (
	<div className="u-card__footer">{children}</div>
);

/* ------------------------------------------------------------------- chip */

export function Chip({
	children,
	meta,
	onClose,
	closeLabel
}: {
	children: ReactNode;
	meta?: ReactNode;
	onClose?: () => void;
	closeLabel?: string;
}) {
	return (
		<span className="u-chip">
			<span className="u-chip__label">{children}</span>
			{meta && <span className="u-chip__meta">{meta}</span>}
			{onClose && (
				<button
					type="button"
					className="u-chip__close"
					aria-label={closeLabel ?? 'Remove'}
					onClick={onClose}
				>
					×
				</button>
			)}
		</span>
	);
}

/* ----------------------------------------------------------------- switch */

export function Switch({
	checked,
	onChange,
	disabled,
	children
}: {
	checked: boolean;
	onChange: (checked: boolean) => void;
	disabled?: boolean;
	children?: ReactNode;
}) {
	return (
		<label className="u-switch">
			<input
				type="checkbox"
				role="switch"
				checked={checked}
				disabled={disabled}
				onChange={event => onChange(event.target.checked)}
			/>
			<span className="u-switch__track">
				<span className="u-switch__thumb" />
			</span>
			{children && <span>{children}</span>}
		</label>
	);
}

/* --------------------------------------------------------------- checkbox */

export function Checkbox({
	checked,
	onChange,
	children,
	label
}: {
	checked: boolean;
	onChange: (checked: boolean) => void;
	children?: ReactNode;
	label?: string;
}) {
	return (
		<label className="u-check" onClick={event => event.stopPropagation()}>
			<input
				type="checkbox"
				checked={checked}
				aria-label={label}
				onChange={event => onChange(event.target.checked)}
			/>
			<span className="u-check__box" aria-hidden="true">
				{checked ? '✓' : ''}
			</span>
			{children}
		</label>
	);
}

/* ----------------------------------------------------------------- select */

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
	size?: 'sm' | 'md';
}

export function Select({ size = 'md', className, children, ...rest }: SelectProps) {
	return (
		<span className="u-select-wrap">
			<select
				className={['u-select', 'sm' === size ? 'u-select--sm' : '', className ?? '']
					.filter(Boolean)
					.join(' ')}
				{...rest}
			>
				{children}
			</select>
			<span className="u-select-arrow" aria-hidden="true">
				▾
			</span>
		</span>
	);
}

/* ------------------------------------------------------------------ modal */

export function Modal({
	open,
	onClose,
	label,
	children
}: {
	open: boolean;
	onClose: () => void;
	label: string;
	children: ReactNode;
}) {
	// Escape closes, and the page behind does not scroll while it is open.
	useEffect(() => {
		if (!open) {
			return;
		}
		const onKey = (event: KeyboardEvent) => {
			if ('Escape' === event.key) {
				onClose();
			}
		};
		document.addEventListener('keydown', onKey);
		const previous = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', onKey);
			document.body.style.overflow = previous;
		};
	}, [open, onClose]);

	if (!open) {
		return null;
	}

	return (
		<div
			className="u-modal-backdrop"
			// Only a click that both starts and ends on the backdrop closes it --
			// otherwise a text selection dragged out of the dialog dismisses it.
			onMouseDown={event => {
				if (event.target === event.currentTarget) {
					onClose();
				}
			}}
		>
			<div className="u-modal" role="dialog" aria-modal="true" aria-label={label}>
				{children}
			</div>
		</div>
	);
}

/* ------------------------------------------------------------------ misc */

export const Spinner = ({ label, size = 'sm' }: { label?: string; size?: Size }) => (
	<span
		className={`u-spinner${'sm' === size ? '' : ` u-spinner--${size}`}`}
		role="status"
		aria-label={label ?? 'Loading'}
	/>
);

export const Divider = () => <hr className="u-divider" />;

export const Skeleton = ({ height = 16, width }: { height?: number; width?: number | string }) => (
	<span className="u-skeleton" style={{ display: 'block', height, width: width ?? '100%' }} />
);
